import { useState, useEffect, useMemo } from 'react';
import {
  Filter, RefreshCw, Download, Calendar, Search, X,
  ChevronDown, ChevronRight, ShoppingCart, Hammer,
  ArrowRightLeft, Settings, Trash2, Package, ArrowDownCircle, ArrowUpCircle,
  Gem, User
} from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import { usePersistentFilters } from '../hooks/usePersistentFilters';
import { SkeletonTable } from './Skeleton';
import { EmptyStateNoData, EmptyStateNoResults } from './EmptyState';
import Pagination from './Pagination';
import SavedFiltersPanel from './SavedFiltersPanel';
import { Location } from '../types';
import './StockMovements.css';

const defaultFilters = {
  item_type: '' as 'METAL' | 'STONE' | '',
  start_date: '',
  end_date: '',
  location_id: '',
  reason: '',
  ref_type: '',
  search: ''
};

export default function StockMovements() {
  const { api } = useApi();
  const { showError, showSuccess } = useToast();
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState<any[]>([]);
  const [expandedBatches, setExpandedBatches] = useState<Set<number>>(new Set());
  const [locations, setLocations] = useState<Location[]>([]);
  const [showSavedFilters, setShowSavedFilters] = useState(false);
  const [activeFilterId, setActiveFilterId] = useState<string | undefined>();
  const view = 'detail';

  // Sistema de filtros persistentes
  const {
    loadActiveFilters,
    saveActiveFilters,
    loadSavedFilters,
    saveFilter,
    deleteFilter,
    applyFilter,
    clearFilters: clearPersistentFilters,
  } = usePersistentFilters('stock-movements', defaultFilters);

  // Filtros
  const [filters, setFilters] = useState(() => loadActiveFilters());

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const reasons = [
    { value: 'WORK_ORDER_COMPLETED', label: 'Producción' },
    { value: 'SALE', label: 'Venta / Salida' },
    { value: 'SALE_HISTORICAL', label: 'Venta Histórica' },
    { value: 'PURCHASE_RECEIPT', label: 'Compra' },
    { value: 'MANUAL_MOVE', label: 'Movimiento Manual' },
    { value: 'MANUAL_ADJUSTMENT', label: 'Ajuste Manual' },
    { value: 'CONSUMPTION', label: 'Consumo Piedras' },
    { value: 'COATING_SENT', label: 'Envío a Revestimiento' },
    { value: 'COATING_RECEIVED', label: 'Recepción Revestimiento' },
    { value: 'LOSS', label: 'Merma / Pérdida' },
    { value: 'RETURN', label: 'Retorno' },
    // Eventos de configuración
    { value: 'CONFIG_MIN_STOCK_CREATED', label: 'Mínimo Creado' },
    { value: 'CONFIG_MIN_STOCK_UPDATED', label: 'Mínimo Actualizado' },
    { value: 'CONFIG_MIN_STOCK_DELETED', label: 'Mínimo Eliminado' },
    { value: 'CONFIG_MIN_STOCK_BULK_UPDATE', label: 'Actualización Masiva Mínimos' },
    { value: 'CONFIG_LOCATION_CREATED', label: 'Ubicación Creada' },
    { value: 'CONFIG_LOCATION_UPDATED', label: 'Ubicación Actualizada' },
    { value: 'CONFIG_LOCATION_DELETED', label: 'Ubicación Eliminada' },
    { value: 'SYSTEM_RECALCULATION', label: 'Recalculación Sistema' },
    { value: 'SYSTEM_RESET', label: 'Reset Sistema' },
    { value: 'SYSTEM_MIGRATION', label: 'Migración Sistema' },
  ];

  const refTypes = [
    { value: 'SALE_IMPORT', label: 'Ventas (Shopify/API)' },
    { value: 'WORK_ORDER', label: 'Orden de Fabricación' },
    { value: 'PURCHASE_ORDER_FUNDICION', label: 'Pedido Fundición' },
    { value: 'STONE_CONSUMPTION', label: 'Consumo Piedras' },
  ];

  useEffect(() => {
    loadLocations();
    // Cargar filtro por defecto si existe
    const saved = loadSavedFilters();
    const defaultFilter = saved.find(f => f.isDefault);
    if (defaultFilter) {
      const appliedFilters = applyFilter(defaultFilter);
      setFilters(appliedFilters);
      setActiveFilterId(defaultFilter.id);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadMovements();
    }, 500); // Debounce para búsqueda
    return () => clearTimeout(timer);
  }, [filters, currentPage, itemsPerPage]);

  // Guardar filtros activos cuando cambian
  useEffect(() => {
    saveActiveFilters(filters);
  }, [filters, saveActiveFilters]);


  const loadLocations = async () => {
    try {
      const response = await api.get('/locations');
      setLocations(response.data);
    } catch (err: any) {
      showError('Error al cargar ubicaciones');
    }
  };

  const loadMovements = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        limit: itemsPerPage,
        ...filters
      };

      const response = await api.get('/stock-movements', { params });
      setBatches(response.data.data || []);
      if (response.data.pagination) {
        setTotalItems(response.data.pagination.total);
        setTotalPages(response.data.pagination.totalPages);
      } else {
        // Fallback para compatibilidad
        setTotalItems(response.data.total || 0);
        setTotalPages(response.data.pages || 0);
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar movimientos');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Resetear a primera página al filtrar
    setActiveFilterId(undefined); // Desactivar filtro guardado al cambiar manualmente
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
    setCurrentPage(1);
    setActiveFilterId(undefined);
    clearPersistentFilters();
  };

  const handleRemoveFilter = (key: string) => {
    setFilters(prev => ({ ...prev, [key]: defaultFilters[key as keyof typeof defaultFilters] }));
    setCurrentPage(1);
  };

  const handleSaveFilter = (name: string, isDefault: boolean) => {
    try {
      saveFilter(name, filters, isDefault);
      showSuccess('Filtro guardado correctamente');
      setShowSavedFilters(false);
    } catch (error) {
      showError('Error al guardar el filtro');
    }
  };

  const handleApplyFilter = (filter: any) => {
    const appliedFilters = applyFilter(filter);
    setFilters(appliedFilters);
    setActiveFilterId(filter.id);
    setCurrentPage(1);
    setShowSavedFilters(false);
    showSuccess(`Filtro "${filter.name}" aplicado`);
  };

  const handleDeleteFilter = (filterId: string) => {
    deleteFilter(filterId);
    if (activeFilterId === filterId) {
      setFilters(defaultFilters);
      setActiveFilterId(undefined);
    }
    showSuccess('Filtro eliminado');
  };

  const toggleBatch = (batchId: number) => {
    setExpandedBatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(batchId)) {
        newSet.delete(batchId);
      } else {
        newSet.add(batchId);
      }
      return newSet;
    });
  };

  const handleExportCSV = async () => {
    try {
      const params: any = {
        ...filters,
        format: 'csv'
      };
      const response = await api.get('/stock-movements/export', {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `movimientos_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSuccess('CSV exportado correctamente');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al exportar CSV');
    }
  };

  const getFilterLabel = (key: string, value: any): string => {
    if (key === 'item_type') {
      return value === 'METAL' ? 'Piezas Metálicas' : value === 'STONE' ? 'Piedras' : '';
    }
    if (key === 'location_id') {
      const location = locations.find(l => l.id.toString() === value);
      return location ? location.nombre : '';
    }
    if (key === 'reason') {
      const reason = reasons.find(r => r.value === value);
      return reason ? reason.label : '';
    }
    if (key === 'ref_type') {
      const type = refTypes.find(t => t.value === value);
      return type ? type.label : value;
    }
    return value;
  };

  // Obtener filtros activos para mostrar como chips
  const activeFilters = Object.entries(filters).filter(([key, value]) => {
    if (key === 'search' && value) return true;
    if (key === 'start_date' && value) return true;
    if (key === 'end_date' && value) return true;
    if (value && value !== defaultFilters[key as keyof typeof defaultFilters]) return true;
    return false;
  });

  // Formatear cantidad sin unidades
  const formatQuantity = (qty: number): string => {
    const absQty = Math.abs(qty);
    const formatted = absQty % 1 === 0 ? absQty.toString() : absQty.toFixed(2);
    return qty < 0 ? `-${formatted}` : `+${formatted}`;
  };

  // Helper para agrupar por días
  const groupedBatches = useMemo(() => {
    const groups: { [key: string]: any[] } = {};
    batches.forEach(batch => {
      const date = new Date(batch.created_at);
      const dayKey = date.toISOString().split('T')[0];
      if (!groups[dayKey]) groups[dayKey] = [];
      groups[dayKey].push(batch);
    });
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [batches]);

  const formatDateHeader = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.getTime() === today.getTime()) return 'Hoy';
    if (date.getTime() === yesterday.getTime()) return 'Ayer';

    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  };

  const getTimeString = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMovementHumanInfo = (batch: any) => {
    const { reason, total_qty, total_items, ref_id, ref_code } = batch;
    const isDeduction = reason === 'SALE' || reason === 'SALE_HISTORICAL' || reason === 'CONSUMPTION' || reason === 'STONE_CONSUMPTION';
    const qty = parseFloat(total_qty);

    switch (reason) {
      case 'SALE':
      case 'SALE_HISTORICAL':
        return {
          icon: <ShoppingCart size={20} />,
          className: 'icon-sale',
          title: ref_code ? `Venta registrada ${ref_code}` : `Venta registrada`,
          description: `Se han descontado ${qty} unidades por ventas.`,
          refLabel: ref_code || (ref_id ? `#${ref_id}` : null)
        };
      case 'WORK_ORDER_COMPLETED':
        return {
          icon: <Hammer size={20} />,
          className: 'icon-production',
          title: `Producción completada`,
          description: `Se han fabricado y entrado ${qty} nuevas piezas.`,
          refLabel: ref_code || (ref_id ? `OF #${ref_id}` : null)
        };
      case 'CONSUMPTION':
      case 'STONE_CONSUMPTION':
        return {
          icon: <Gem size={20} />,
          className: 'icon-consumption',
          title: `Consumo de Piedras`,
          description: `Se han utilizado ${qty} ristras de piedras.`,
          refLabel: ref_code || (ref_id ? `Ref: ${ref_id}` : null)
        };
      case 'MANUAL_MOVE':
        return {
          icon: <ArrowRightLeft size={20} />,
          className: 'icon-transfer',
          title: `Traslado manual`,
          description: batch.from_location_name && batch.to_location_name
            ? `Movimiento de ${batch.from_location_name} a ${batch.to_location_name}.`
            : `Movimiento de stock entre ubicaciones.`,
          refLabel: null
        };
      case 'PURCHASE_RECEIPT':
        return {
          icon: <Package size={20} />,
          className: 'icon-purchase',
          title: `Entrada de compra`,
          description: `Recepción de ${qty} unidades de proveedor.`,
          refLabel: ref_code || (ref_id ? `Pedido #${ref_id}` : null)
        };
      case 'COATING_SENT':
        return {
          icon: <ArrowUpCircle size={20} />,
          className: 'icon-transfer',
          title: `Envío a revestimiento`,
          description: `Piezas enviadas para baño/chapado.`,
          refLabel: ref_code || (ref_id ? `Ref #${ref_id}` : null)
        };
      case 'COATING_RECEIVED':
        return {
          icon: <ArrowDownCircle size={20} />,
          className: 'icon-production',
          title: `Recepción revestimiento`,
          description: `Piezas recibidas de baño/chapado.`,
          refLabel: ref_code || (ref_id ? `Ref #${ref_id}` : null)
        };
      case 'MANUAL_ADJUSTMENT':
        return {
          icon: <Settings size={20} />,
          className: 'icon-adjustment',
          title: `Ajuste de inventario`,
          description: `Corrección manual de stock.`,
          refLabel: null
        };
      case 'LOSS':
        return {
          icon: <Trash2 size={20} />,
          className: 'icon-adjustment',
          title: `Merma / Pérdida`,
          description: `Ajuste por piezas defectuosas o pérdida.`,
          refLabel: null
        };
      case 'RETURN':
        return {
          icon: <ArrowDownCircle size={20} />,
          className: 'icon-purchase',
          title: `Retorno / Devolución`,
          description: `Se han devuelto ${qty} unidades al inventario.`,
          refLabel: ref_code || (ref_id ? `Ref #${ref_id}` : null)
        };
      case 'CONFIG_MIN_STOCK_CREATED':
      case 'CONFIG_MIN_STOCK_UPDATED':
      case 'CONFIG_MIN_STOCK_DELETED':
      case 'CONFIG_MIN_STOCK_BULK_UPDATE':
        return {
          icon: <Settings size={20} />,
          className: 'icon-adjustment',
          title: `Cambio en Stock Mínimo`,
          description: `Se ha modificado la configuración de alertas de stock.`,
          refLabel: null
        };
      case 'SYSTEM_RECALCULATION':
        return {
          icon: <RefreshCw size={20} />,
          className: 'icon-adjustment',
          title: `Recalculación de Sistema`,
          description: `El sistema ha actualizado los niveles basándose en el historial.`,
          refLabel: null
        };
      default:
        return {
          icon: <RefreshCw size={20} />,
          className: 'icon-adjustment',
          title: reason.replace(/_/g, ' '),
          description: `Acción del sistema sobre ${total_items} items.`,
          refLabel: ref_code || (ref_id ? `#${ref_id}` : null)
        };
    }
  };

  return (
    <div className="tab-content">
      {/* Header con título y botones */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div>
            <h2 style={{ margin: 0 }}>Historial de Movimientos</h2>
          </div>
          {/* Botones de acción */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              className="btn btn-secondary"
              onClick={() => setShowSavedFilters(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <Filter size={16} />
              Filtros
            </button>
            <button
              className="btn btn-secondary"
              onClick={loadMovements}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <RefreshCw size={16} />
              Actualizar
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleExportCSV}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <Download size={16} />
              Exportar CSV
            </button>
          </div>
        </div>

        {/* Filtros activos */}
        {activeFilters.length > 0 && (
          <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
              FILTROS ACTIVOS:
            </span>
            {activeFilters.map(([key, value]) => {
              const label = getFilterLabel(key, value);
              if (!label) return null;
              return (
                <span
                  key={key}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.375rem 0.75rem',
                    background: '#fef3c7',
                    color: '#92400e',
                    borderRadius: '9999px',
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                >
                  <span>✓</span>
                  <span>{label}</span>
                  <button
                    onClick={() => handleRemoveFilter(key)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      color: '#92400e'
                    }}
                  >
                    <X size={14} />
                  </button>
                </span>
              );
            })}
            <button
              onClick={clearFilters}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--accent)',
                cursor: 'pointer',
                fontSize: '0.875rem',
                textDecoration: 'underline',
                padding: 0
              }}
            >
              Limpiar todo
            </button>
          </div>
        )}

        {/* Filtros de búsqueda */}
        <div style={{
          display: 'flex',
          gap: '0.75rem',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          marginBottom: '1rem'
        }}>
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
              Búsqueda
            </label>
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
              <input
                type="text"
                className="input"
                placeholder="Código, nombre o usua"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                style={{ paddingLeft: '2.5rem' }}
              />
            </div>
          </div>

          <div style={{ flex: '0 0 150px' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
              Fecha Inicio
            </label>
            <div style={{ position: 'relative' }}>
              <Calendar size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', pointerEvents: 'none' }} />
              <input
                type="date"
                className="input"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                style={{ paddingLeft: '2.5rem' }}
              />
            </div>
          </div>

          <div style={{ flex: '0 0 150px' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
              Fecha Fin
            </label>
            <div style={{ position: 'relative' }}>
              <Calendar size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', pointerEvents: 'none' }} />
              <input
                type="date"
                className="input"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                style={{ paddingLeft: '2.5rem' }}
              />
            </div>
          </div>

          <div style={{ flex: '0 0 180px' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
              Tipo de Item
            </label>
            <select
              className="input"
              value={filters.item_type}
              onChange={(e) => handleFilterChange('item_type', e.target.value)}
            >
              <option value="">Todos</option>
              <option value="METAL">Piezas Metálicas</option>
              <option value="STONE">Piedras</option>
            </select>
          </div>

          <div style={{ flex: '0 0 200px' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
              Ubicación / Origen
            </label>
            <select
              className="input"
              value={filters.location_id}
              onChange={(e) => handleFilterChange('location_id', e.target.value)}
            >
              <option value="">Todas</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.nombre}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <SavedFiltersPanel
        savedFilters={loadSavedFilters()}
        activeFilterId={activeFilterId}
        onApply={handleApplyFilter}
        onDelete={handleDeleteFilter}
        onSave={handleSaveFilter}
        onClose={() => setShowSavedFilters(false)}
        isOpen={showSavedFilters}
      />

      {loading ? (
        <SkeletonTable rows={10} columns={1} />
      ) : (
        <div className="stock-movements-container">
          {batches.length === 0 ? (
            <div style={{ background: 'var(--bg-primary)', padding: '4rem', textAlign: 'center', border: '1px solid var(--border-color)' }}>
              {filters.search || filters.reason || filters.ref_type ? (
                <EmptyStateNoResults onClearFilters={clearFilters} />
              ) : (
                <EmptyStateNoData />
              )}
            </div>
          ) : (
            groupedBatches.map(([date, dayBatches]) => (
              <div key={date} className="movement-day-group">
                <div className="day-header">
                  <Calendar size={14} />
                  {formatDateHeader(date)}
                </div>

                {dayBatches.map(batch => {
                  const info = getMovementHumanInfo(batch);
                  const isExpanded = expandedBatches.has(batch.id);
                  const hasMovements = batch.movements && batch.movements.length > 0;

                  // Negar cantidad si es una venta o consumo para visualización coherente
                  let qty = parseFloat(batch.total_qty || 0);
                  const isDeduction = batch.reason === 'SALE' || batch.reason === 'SALE_HISTORICAL' || batch.reason === 'CONSUMPTION' || batch.reason === 'STONE_CONSUMPTION';
                  if (isDeduction && qty > 0) qty = -qty;

                  return (
                    <div key={batch.id} className="movement-card">
                      <div className={`movement-icon-container ${info.className}`}>
                        {info.icon}
                      </div>

                      <div className="movement-content">
                        <div className="movement-top-info">
                          <div>
                            <div className="movement-time">{getTimeString(batch.created_at)}</div>
                            <div className="movement-description">{info.title}</div>
                          </div>

                          <div className="movement-qty-container">
                            <div className={`movement-qty ${qty > 0 ? 'qty-positive' : qty < 0 ? 'qty-negative' : 'qty-neutral'}`}>
                              {formatQuantity(qty)}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                              {batch.total_items} {batch.total_items === 1 ? 'item' : 'items'}
                            </div>
                          </div>
                        </div>

                        <div className="movement-meta">
                          <span className="movement-description" style={{ fontSize: '0.875rem', fontWeight: 400, color: 'var(--text-secondary)' }}>
                            {info.description}
                          </span>
                          {info.refLabel && <span className="movement-ref">{info.refLabel}</span>}
                          <span className="movement-user">
                            <User size={12} />
                            {batch.created_by || 'Sistema'}
                          </span>
                        </div>

                        {hasMovements && (
                          <div className="movement-footer">
                            <button
                              className="btn btn-ghost"
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', color: 'var(--accent)' }}
                              onClick={() => toggleBatch(batch.id)}
                            >
                              {isExpanded ? 'Ocultar detalles' : 'Ver piezas involucradas'}
                              {isExpanded ? <ChevronDown size={14} style={{ marginLeft: '4px' }} /> : <ChevronRight size={14} style={{ marginLeft: '4px' }} />}
                            </button>
                          </div>
                        )}

                        {isExpanded && hasMovements && (
                          <div className="items-summary-list animate-fade-in">
                            {batch.movements.map((move: any) => (
                              <div key={move.id} className="item-detail-row">
                                <div className="item-detail-info">
                                  <span className="item-detail-code">{move.cod_item}</span>
                                  <span className="item-detail-name">{move.nombre}</span>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                    {(isDeduction && move.qty > 0) ? `-${move.qty}` : move.qty > 0 ? `+${move.qty}` : move.qty}
                                  </div>
                                  <div className="item-detail-nodes" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                                      {move.from_location_name || '-'}
                                      {move.stock_before_from !== undefined && move.stock_after_from !== undefined && (
                                        <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', opacity: 0.8 }}>
                                          ({move.stock_before_from} → {move.stock_after_from})
                                        </span>
                                      )}
                                      {move.to_location_name && ' → '}
                                      {move.to_location_name}
                                      {move.stock_before_to !== undefined && move.stock_after_to !== undefined && (
                                        <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', opacity: 0.8 }}>
                                          ({move.stock_before_to} → {move.stock_after_to})
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(limit) => {
              setItemsPerPage(limit);
              setCurrentPage(1);
            }}
            showItemsPerPage={true}
          />
        </div>
      )}
    </div>
  );
}
